import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, Eye } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchBooks, deleteBook } from '../../store/books/booksSlice';
import { Button, Modal, TableRowSkeleton, EmptyState, useToast } from '../../components/common';
import { Book } from '../../types';

export const ManageBooksPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { books, isLoading, totalPages, page } = useAppSelector(
    (state) => state.books
  );
  const [search, setSearch] = useState('');
  const [deleteModal, setDeleteModal] = useState<Book | null>(null);

  useEffect(() => {
    dispatch(fetchBooks({ page: 1, limit: 10, search: search || undefined }));
  }, [dispatch, search]);

  const handlePageChange = (newPage: number) => {
    dispatch(fetchBooks({ page: newPage, limit: 10, search: search || undefined }));
  };

  const handleDelete = async () => {
    if (deleteModal) {
      try {
        await dispatch(deleteBook(deleteModal._id)).unwrap();
        showToast(`"${deleteModal.title}" has been deleted`, 'success');
      } catch {
        showToast('Failed to delete book', 'error');
      }
      setDeleteModal(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">Manage Books</h1>
          <p className="text-sm sm:text-base text-slate-500">Add, edit, and remove books from the library</p>
        </div>
        <Link to="/admin/books/add" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto justify-center">
            <Plus size={18} className="mr-1 sm:mr-2 sm:w-5 sm:h-5" />
            Add Book
          </Button>
        </Link>
      </div>

      <div className="relative mb-4 sm:mb-6">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 sm:w-5 sm:h-5" />
        <input
          type="text"
          placeholder="Search books..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Cover</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Author</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">ISBN</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRowSkeleton key={i} columns={6} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {books.map((book) => (
              <div key={book._id} className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex gap-3">
                  <div className="w-16 h-24 rounded overflow-hidden bg-slate-100 flex-shrink-0">
                    {book.coverImage ? (
                      <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg">
                        {book.title.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">{book.title}</h3>
                    <p className="text-sm text-slate-600 mb-2">{book.author}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Category:</span>
                        <span className="text-slate-900">{book.category || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">ISBN:</span>
                        <span className="text-slate-900 font-mono text-xs">{book.isbn}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        className="flex-1 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center justify-center gap-1"
                        onClick={() => navigate(`/books/${book._id}`)}
                      >
                        <Eye size={16} />
                        View
                      </button>
                      <button
                        className="flex-1 px-3 py-1.5 text-sm text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors flex items-center justify-center gap-1"
                        onClick={() => navigate(`/admin/books/edit/${book._id}`)}
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                      <button
                        className="flex-1 px-3 py-1.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center justify-center gap-1"
                        onClick={() => setDeleteModal(book)}
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Cover</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Title</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Author</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">ISBN</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {books.map((book) => (
                    <tr key={book._id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="w-10 h-14 rounded overflow-hidden bg-slate-100">
                          {book.coverImage ? (
                            <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                              {book.title.charAt(0)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900 max-w-xs truncate">{book.title}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{book.author}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-[120px] truncate">{book.category || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-[150px] truncate">{book.isbn}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            onClick={() => navigate(`/books/${book._id}`)}
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                            onClick={() => navigate(`/admin/books/edit/${book._id}`)}
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            onClick={() => setDeleteModal(book)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {books.length === 0 && (
            <EmptyState
              variant="books"
              title="No books found"
              description={search ? "Try adjusting your search to find what you're looking for." : "Start building your library by adding your first book."}
              action={!search ? { label: 'Add Book', onClick: () => navigate('/admin/books/add') } : undefined}
            />
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 sm:gap-4 mt-4 sm:mt-6">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="text-xs sm:text-sm text-slate-600">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Confirm Delete"
        size="sm"
      >
        <p className="text-slate-700">Are you sure you want to delete "{deleteModal?.title}"?</p>
        <p className="text-sm text-slate-500 mt-2">
          This will soft delete the book. You can restore it later from the Deleted Books page.
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={() => setDeleteModal(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};
